import React, { useState } from "react";
import { MentorAvatar } from "./Logo";
import { Sparkles, ArrowRight, Check, Compass, TrendingUp, BarChart2, Star } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

export default function CareerConfusionPage({ onOpenModal }) {
  const [activeNode, setActiveTab] = useState("AI");
  const [quizStep, setQuizStep] = useState(0);
  const [quizData, setQuizData] = useState({ stack: "", exp: "", focus: "" });
  const [quizScore, setQuizScore] = useState(null);

  const careerNodes = {
    AI: {
      title: "Entry Software Associate / AI Dev",
      salary: "₹6L - ₹15L",
      difficulty: "Medium",
      skills: "Python, simple model integrations, database operations, RAG workflows",
      desc: "An incredible entry point for Tier 2/3 candidates looking to start in top modern software offices."
    },
    Backend: {
      title: "Core Backend Engineer",
      salary: "₹5L - ₹12L",
      difficulty: "Medium",
      skills: "Node.js, Express, REST APIs, SQL, cloud container deployment",
      desc: "Robust architectural path that skips college GPA requirements by proving real-world backend API builds."
    },
    Frontend: {
      title: "Frontend / React Engineer",
      salary: "₹4L - ₹10L",
      difficulty: "Approach-friendly",
      skills: "HTML, CSS, modern JavaScript, Tailwind CSS, responsive state management",
      desc: "Build fast-rendering responsive user interfaces that stand out immediately in recruitment drives."
    }
  };

  const chartData = [
    { year: "Current", ctc: 2.5, vph_ctc: 2.5 },
    { year: "Year 1", ctc: 3.2, vph_ctc: 6 },
    { year: "Year 2", ctc: 4.0, vph_ctc: 10 },
    { year: "Year 3", ctc: 5.0, vph_ctc: 15 }
  ];

  const processQuiz = (field, val) => {
    const updated = { ...quizData, [field]: val };
    setQuizData(updated);
    if (quizStep < 2) {
      setQuizStep(quizStep + 1);
    } else {
      // Calculate customized alignment roadmap
      setQuizScore({
        path: updated.focus === "AI" ? "AI Developer Associate" : "Full-Stack Software Specialist",
        jump: updated.exp === "Fresher" ? "150% Placement Launch" : "180% Salary Accelerator",
        recommendation: "⚠️ COACHING ALIGNMENT: Don't fall for expensive offline certification institutes that promise 'guaranteed jobs' but give outdated syllabi. Mr. Vedula's Career Node maps you directly to the latest skills in demand."
      });
    }
  };

  return (
    <div className="bg-[#0F0728] text-white font-sans pt-20">
      
      {/* 1. Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#2E1065]/10 to-[#0F0728] py-16 md:py-24 border-b border-[#3B1F75]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,#581C87,transparent_35%)] opacity-30" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest bg-amber-500/10 text-[#F59E0B] border border-amber-500/20">
              <Sparkles className="h-3 w-3" /> The Small Town Guide
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[1.1]" data-testid="hero-heading">
              Saturated syllabi, <span className="text-[#F59E0B] block">no guide?</span>
            </h1>
            
            <h2 className="text-lg sm:text-xl font-extrabold text-slate-300 tracking-tight leading-relaxed">
              Unlock Your Career Clarity: Skip expensive coaching scams and discover your high-demand software path.
            </h2>
            
            <p className="text-[#D8B4FE] text-sm sm:text-base max-w-xl leading-relaxed">
              Many local offline centers charge hefty fees and teach ancient curricula. We guide you step-by-step from foundational software blocks directly into high-paying modern roles.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Career Confusion Strategy")}
                className="px-6 py-4 bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#FBBF24] hover:to-[#F59E0B] text-slate-950 font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition-all"
                data-testid="book-free-clarity-call-btn"
              >
                Book Free Clarity Call <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Career Confusion Strategy")}
                className="px-6 py-4 bg-zinc-900 hover:bg-zinc-800 text-white font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 transition-all border border-slate-800"
                data-testid="register-for-free-webinar-btn"
              >
                Register for Free Webinar
              </button>
            </div>
          </div>

          {/* Right Column: Constellation Quiz / Radar Chart Widget */}
          <div className="lg:col-span-5 bg-[#1E1145] border border-[#3B1F75] rounded-3xl p-6 shadow-2xl relative">
            <div className="absolute top-0 right-0 w-24 h-24 bg-[#F59E0B]/5 rounded-full blur-3xl" />
            
            <div className="flex items-center justify-between border-b border-[#3B1F75] pb-3 mb-4">
              <span className="text-xs font-black uppercase tracking-wider text-[#F59E0B] flex items-center gap-1">
                <Compass className="h-3.5 w-3.5 animate-spin" /> Trajectory Pathfinder Diagnostics
              </span>
              <span className="text-[10px] text-[#C084FC] font-bold">Step {quizStep + 1} of 3</span>
            </div>

            {quizScore === null ? (
              <div className="space-y-4">
                {quizStep === 0 && (
                  <div className="space-y-3 animate-fade-in">
                    <span className="text-xs font-bold text-[#C084FC] uppercase tracking-wider block">What is your current tech stack experience?</span>
                    <div className="grid grid-cols-1 gap-2">
                      {["React / Frontend", "Node.js / Java / Backend", "Python / ML", "Manual/Automation Testing"].map((opt) => (
                        <button
                          key={opt}
                          onClick={() => processQuiz("stack", opt)}
                          className="w-full text-left p-3.5 bg-slate-950 hover:bg-[#3B1F75]/40 border border-slate-800/80 hover:border-[#F59E0B]/40 rounded-xl text-sm transition-all"
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {quizStep === 1 && (
                  <div className="space-y-3 animate-fade-in">
                    <span className="text-xs font-bold text-[#C084FC] uppercase tracking-wider block">Your years of experience?</span>
                    <div className="grid grid-cols-2 gap-2">
                      {["Fresher", "1-3 Years", "3-6 Years", "6+ Years"].map((opt) => (
                        <button
                          key={opt}
                          onClick={() => processQuiz("exp", opt)}
                          className="text-center p-3.5 bg-slate-950 hover:bg-[#3B1F75]/40 border border-slate-800 rounded-xl text-sm transition-all"
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {quizStep === 2 && (
                  <div className="space-y-3 animate-fade-in">
                    <span className="text-xs font-bold text-[#C084FC] uppercase tracking-wider block">Your primary interest?</span>
                    <div className="grid grid-cols-2 gap-2">
                      {["AI / Machine Learning", "Systems Architecture", "Full Stack Dev", "Engineering Lead"].map((opt) => (
                        <button
                          key={opt}
                          onClick={() => processQuiz("focus", opt)}
                          className="text-center p-3.5 bg-slate-950 hover:bg-[#3B1F75]/40 border border-slate-800 rounded-xl text-sm transition-all"
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 animate-fade-in text-xs">
                <div className="p-3.5 bg-slate-950 border border-[#3B1F75] rounded-xl text-[#F59E0B] font-bold">
                  <div>Recommended: {quizScore.path}</div>
                  <div className="text-[10px] text-[#C084FC] mt-0.5">Potential Jump: {quizScore.jump}</div>
                </div>
                
                <p className="text-[#D8B4FE] leading-relaxed font-semibold">
                  {quizScore.recommendation}
                </p>

                <button
                  onClick={() => { setQuizStep(0); setQuizScore(null); }}
                  className="w-full py-2 bg-slate-950 border border-slate-800 hover:bg-[#3B1F75] rounded-lg font-bold text-xs uppercase"
                >
                  Retake Pathfinder Quiz
                </button>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* 2. Pain Identification */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs font-black uppercase tracking-wider text-[#F59E0B]">The Local Trap</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mt-2 mb-4" data-testid="pain-heading">
            Is this your struggle?
          </h2>
          <div className="space-y-4 text-lg text-[#D8B4FE] font-medium">
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Overwhelmed by random online certification courses with no placement results.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Feeling trapped in basic roles because you don't have senior metro guidance.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Coping with endless, contradictory roadmaps instead of building industry skills.
            </p>
          </div>
          <div className="mt-6 border-l-4 border-[#F59E0B] pl-4 py-2 italic font-black text-[#D8B4FE] text-lg md:text-xl leading-relaxed">
            "It’s not just rejection. It’s the frustration of spending hard-earned family money on offline centers that teach outdated theories."
          </div>
        </div>
      </section>

      {/* 3. Behind the Pain (Storytelling Case Study) */}
      <section className="bg-[#1E1145]/40 py-16 border-t border-b border-[#3B1F75]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4 flex justify-center">
            <MentorAvatar />
          </div>
          <div className="md:col-span-8 space-y-4">
            <span className="text-xs font-black uppercase tracking-wider text-[#F59E0B]">Small Town Transformation</span>
            <h3 className="text-2xl font-extrabold text-white tracking-tight">
              Akash was stuck in a data-entry local center, dreaming of writing code.
            </h3>
            <p className="text-[#D8B4FE] leading-relaxed text-sm sm:text-base">
              "I wanted to build websites, but my local center just had me copying spreadsheets. I had no mentor to guide me. When I joined Mr. Vedula's program, he mapped out a clear modern software path for me. I stopped copying theory and focused strictly on real React portfolios. Today, I work remotely for a major tech startup!"
            </p>
            <div className="flex items-center gap-4 pt-2">
              <div className="text-xs font-bold uppercase tracking-wider bg-purple-500/10 text-purple-300 px-3 py-1 rounded-full border border-purple-500/20">
                Remote Developer Role
              </div>
              <div className="text-xs font-bold uppercase tracking-wider bg-amber-500/10 text-[#F59E0B] px-3 py-1 rounded-full border border-amber-500/20">
                150% Career Growth
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trajectory Growth Projection Chart */}
      <section className="py-16 bg-slate-950 border-b border-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
          <span className="text-xs font-black uppercase tracking-wider text-[#F59E0B]">Salary Projection Estimator</span>
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Your Salary Velocity Comparison</h3>
          <p className="text-zinc-400 text-sm max-w-xl mx-auto">
            See the massive difference between a standard developer trajectory vs Mr. Vedula’s senior career steering system (in LPA).
          </p>

          <div className="bg-[#1E1145]/20 p-5 rounded-3xl border border-[#3B1F75] h-[300px]">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData}>
                <XAxis dataKey="year" stroke="#8884d8" />
                <YAxis stroke="#8884d8" />
                <Tooltip />
                <Area type="monotone" dataKey="vph_ctc" name="VPH Blueprint Pathway" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.15} strokeWidth={3} />
                <Area type="monotone" dataKey="ctc" name="Standard Saturated Path" stroke="#3B1F75" fill="#3B1F75" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* 4. Solution Framework */}
      <section className="py-16 md:py-24 bg-[#0F0728] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-black uppercase tracking-wider text-[#F59E0B]">The Alignment</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="solution-heading">
              Your Career GPS System
            </h2>
            <p className="text-[#D8B4FE] text-sm leading-relaxed">
              Our structured career system organizes your engineering expertise into direct, high-value alignments.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 font-sans">
            <div className="bg-[#1E1145]/40 border border-[#3B1F75]/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 text-[#F59E0B] flex items-center justify-center font-black">01</div>
              <h3 className="font-extrabold text-lg text-white">Resume Rescue</h3>
              <p className="text-[#D8B4FE] text-sm">
                Translate standard engineering tasks into high-impact, business-driven, ATS-destroyer achievements.
              </p>
            </div>
            
            <div className="bg-[#1E1145]/40 border border-[#3B1F75]/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 text-[#F59E0B] flex items-center justify-center font-black">02</div>
              <h3 className="font-extrabold text-lg text-white">Interview Anchor</h3>
              <p className="text-[#D8B4FE] text-sm">
                Confidently script STAR scenarios and system design reviews, eliminating nervousness.
              </p>
            </div>

            <div className="bg-[#1E1145]/40 border border-[#3B1F75]/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 text-[#F59E0B] flex items-center justify-center font-black">03</div>
              <h3 className="font-extrabold text-lg text-white">Gap Fillers</h3>
              <p className="text-[#D8B4FE] text-sm">
                Incorporate robust docker models, scale multi-tenant architectures, and produce real proof of development.
              </p>
            </div>

            <div className="bg-[#1E1145]/40 border border-[#3B1F75]/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 text-[#F59E0B] flex items-center justify-center font-black">04</div>
              <h3 className="font-extrabold text-lg text-white">Dream 50 Tracker</h3>
              <p className="text-[#D8B4FE] text-sm">
                Identify, track, and bypass gates at the top 50 organizations seeking senior dev assets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Silver Membership Pitch */}
      <section className="py-16 md:py-24 bg-[#0F0728] border-t border-[#3B1F75]/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2E1065] to-[#581C87] text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl font-sans">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative">
            <div className="md:col-span-8 space-y-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-amber-400 text-slate-950">
                👑 Premium Curriculum Package
              </span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white" data-testid="silver-heading">
                Upgrade to Silver Membership
              </h2>
              <div className="space-y-3 text-[#D8B4FE] text-sm">
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>90-Day Production Playbook</strong> (Direct trajectory map frameworks)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Gap Fillers Workbook</strong> (Production architecture guidelines)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Resume Rescue Blueprint</strong> (Elite copy template systems)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Interview Anchor Toolkit</strong> (Behavioral STAR worksheets)
                </p>
              </div>
              <div className="pt-2">
                <span className="text-xs text-amber-300 font-bold block mb-1">🎁 EXCLUSIVE BONUSES INCLUDED:</span>
                <span className="text-xs text-purple-100 font-medium">3 Free 1-on-1 Clarity Calls + Weekly Lifetime Q&A Mastermind Webinars</span>
              </div>
            </div>
            
            <div className="md:col-span-4 bg-black/25 border border-white/10 p-6 rounded-2xl text-center space-y-4">
              <div className="text-purple-200 text-xs font-black uppercase">Immediate Lifetime Access</div>
              <div className="text-4xl font-black text-amber-400">₹4,999</div>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a187ad45eaf79ab7261b65e", "Join Silver Membership Now", "Confusion Strategy Silver Membership")}
                className="w-full py-4 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-sm uppercase rounded-xl shadow-lg transition-all"
                data-testid="join-silver-membership-btn"
              >
                Join Silver Now
              </button>
              <span className="text-[10px] text-purple-200 block">Immediate Digital Checkout</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Options Section */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 font-sans">
        <div className="text-center space-y-2">
          <span className="text-xs font-black uppercase tracking-wider text-[#F59E0B]">Secure Your Slot</span>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Steering Acceleration Pathways</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-[#3B1F75] bg-[#1E1145]/20 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-[#F59E0B] uppercase tracking-widest block">1-on-1 DIAGNOSTIC</span>
              <h3 className="font-extrabold text-xl text-white">Free Clarity Call</h3>
              <p className="text-[#D8B4FE] text-sm">
                Map out an elite growth trajectory based on your personalized Pathfinder quiz results.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Confusion Strategy Free Call")}
              className="w-full mt-6 py-3.5 bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#FBBF24] hover:to-[#F59E0B] text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-clarity-call-btn"
            >
              Book Call (3 slots left)
            </button>
          </div>

          <div className="border border-[#3B1F75] bg-[#1E1145]/20 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-[#F59E0B] uppercase tracking-widest block">GROWTH WEBINAR</span>
              <h3 className="font-extrabold text-xl text-white">Exclusive Live Webinar</h3>
              <p className="text-[#D8B4FE] text-sm">
                Learn the quantitative filters to bypass saturation and switch directly into premium engineering roles.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Confusion Strategy Live Webinar")}
              className="w-full mt-6 py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-webinar-btn"
            >
              Register for Webinar
            </button>
          </div>

          <div className="border border-amber-500/30 bg-amber-500/5 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-amber-500 uppercase tracking-widest block">TRAJECTORY TOOLKIT</span>
              <h3 className="font-extrabold text-xl text-slate-100">Silver Membership</h3>
              <p className="text-[#D8B4FE] text-sm">
                Full workbook materials, behavioural and technical diagnostic checklists, and priority mentor Q&A access.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://vedulaprogress.lovable.app", "Join Silver Membership Now", "Confusion Strategy Silver Membership")}
              className="w-full mt-6 py-3.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-silver-btn"
            >
              Join Silver Membership
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
